import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useSubmitNpsResponse } from '@area-restrita/api-client/endpoints/nps/nps.gen';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import {
  NPS_COMMENT_MAX_LENGTH,
  npsResponseSchema,
  type NpsResponseFormData,
} from './schema';
import type {
  NpsCampaignSnapshot,
  NpsResponseSummary,
  NpsSchedule,
} from './types';
import { getNpsSubmitErrorMessage, getResponseStatus } from './utils';

interface ResponseStepProps {
  campaign: NpsCampaignSnapshot;
  selectedSchedules: NpsSchedule[];
  onChangeSchedules: () => void;
  onClose: () => void;
  onStaleCampaign: () => void;
  onSuccess: (summary: NpsResponseSummary) => void;
}

const ResponseStep: React.FC<ResponseStepProps> = ({
  campaign,
  selectedSchedules,
  onChangeSchedules,
  onClose,
  onStaleCampaign,
  onSuccess,
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submitResponse = useSubmitNpsResponse();
  const responseForm = useForm<NpsResponseFormData>({
    resolver: zodResolver(npsResponseSchema),
    defaultValues: { comment: '' },
  });
  const score = responseForm.watch('score');
  const comment = responseForm.watch('comment');
  const submit = responseForm.handleSubmit((data) => {
    setSubmitError(null);
    submitResponse.mutate(
      {
        campaignId: campaign.id,
        data: {
          buslineScheduleIds: selectedSchedules.map(({ id }) => id),
          score: data.score,
          comment: data.comment.trim() || undefined,
        },
      },
      {
        onSuccess: () =>
          onSuccess({
            campaign,
            scheduleCount: selectedSchedules.length,
            score: data.score,
          }),
        onError: (error) => {
          setSubmitError(getNpsSubmitErrorMessage(error));
          const status = getResponseStatus(error);
          if (status === 409 || status === 410) onStaleCampaign();
        },
      },
    );
  });

  return (
    <>
      <DialogHeader>
        <DialogDescription>{campaign.name}</DialogDescription>
        <DialogTitle>{campaign.question}</DialogTitle>
      </DialogHeader>
      <div className="rounded-md border p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span>
            {selectedSchedules.length === 1
              ? '1 horário selecionado'
              : `${selectedSchedules.length} horários selecionados`}
          </span>
          <Button variant="link" onClick={onChangeSchedules}>
            Alterar seleções
          </Button>
        </div>
        {selectedSchedules.map((schedule) => (
          <p key={schedule.id} className="text-muted-foreground">
            {schedule.code} - {schedule.name}, {schedule.startAt} -{' '}
            {schedule.endAt}
          </p>
        ))}
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Sua nota</legend>
        <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
          {Array.from({ length: 10 }, (_, index) => index + 1).map((value) => (
            <Button
              key={value}
              type="button"
              variant={score === value ? 'default' : 'outline'}
              size="icon"
              aria-pressed={score === value}
              onClick={() =>
                responseForm.setValue('score', value, { shouldValidate: true })
              }
            >
              {value}
            </Button>
          ))}
        </div>
      </fieldset>
      <div className="space-y-2">
        <Label htmlFor="nps-comment">Comentário</Label>
        <Textarea
          id="nps-comment"
          maxLength={NPS_COMMENT_MAX_LENGTH + 1}
          {...responseForm.register('comment', {
            onChange: () => setSubmitError(null),
          })}
          placeholder="Conte um pouco mais sobre sua experiência."
        />
        <div className="flex justify-between gap-3 text-xs text-muted-foreground">
          <span>Não inclua dados pessoais no comentário.</span>
          <span>
            {comment.length}/{NPS_COMMENT_MAX_LENGTH}
          </span>
        </div>
      </div>
      {responseForm.formState.errors.score ? (
        <p role="alert" className="text-sm text-destructive">
          Escolha uma nota de 1 a 10 para continuar.
        </p>
      ) : null}
      {responseForm.formState.errors.comment ? (
        <p role="alert" className="text-sm text-destructive">
          {responseForm.formState.errors.comment.message}
        </p>
      ) : null}
      {submitError ? (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      ) : null}
      <DialogFooter>
        <Button
          variant="ghost"
          disabled={submitResponse.isPending}
          onClick={onClose}
        >
          Responder depois
        </Button>
        <Button loading={submitResponse.isPending} onClick={submit}>
          Enviar resposta
        </Button>
      </DialogFooter>
    </>
  );
};

export default ResponseStep;
